import React, { useCallback, useMemo } from 'react';

import { UIAnalyticsEvent } from '@atlaskit/analytics-next';

import Modal, {
  ActionProps,
  KeyboardOrMouseEvent,
} from 'src/components/modal-dialog-actions';
import { useIntl } from 'src/hooks/intl';

type Props = {
  analyticsEvent?: UIAnalyticsEvent;
  onConfirmDelete: (_value: any, analyticsEvent?: UIAnalyticsEvent) => void;
  onHideModal: () => void;
};

const messages = {
  deleteCommmentLabel: {
    id: 'frontbucket.components.conversation.deleteCommentDialog.delete',
    message:
      'The label displayed on the button to confirm the deletion of a comment',
    defaultMessage: 'Delete',
  },
  cancelDeletionLabel: {
    id: 'frontbucket.components.conversation.deleteCommentDialog.cancel',
    message:
      'The label displayed on the button to cancel the deletion of a comment',
    defaultMessage: 'Cancel',
  },
  deleteCommentConfirmationHeading: {
    id: 'frontbucket.components.conversation.deleteCommentDialog.heading',
    message:
      'The header displayed in the delete comment dialog when requesting confirmation to delete a comment',
    defaultMessage: 'Delete comment',
  },
  deleteCommentConfirmationMessage: {
    id: 'frontbucket.components.conversation.deleteCommentDialog.message',
    message:
      'The message displayed in the delete comment dialog when requesting confirmation to delete a comment',
    defaultMessage: 'Are you sure you want to delete this comment?',
  },
};

const BaseDeleteCommentModal = (props: Props) => {
  const intl = useIntl();
  const { analyticsEvent, onConfirmDelete, onHideModal } = props;
  const modalActions: ActionProps[] = useMemo(() => {
    return [
      {
        text: intl.formatMessage(messages.cancelDeletionLabel),
        onClick: () => {
          onHideModal();
        },
      },
      {
        text: intl.formatMessage(messages.deleteCommmentLabel),
        onClick: () => {
          onConfirmDelete(undefined, analyticsEvent);
          onHideModal();
        },
      },
    ];
  }, [analyticsEvent, onConfirmDelete, onHideModal, intl]);

  const isKeyboardEvent = (
    event: KeyboardOrMouseEvent
  ): event is React.KeyboardEvent =>
    (event as React.KeyboardEvent).key !== undefined;

  const onCloseHandler = useCallback(
    (event: KeyboardOrMouseEvent) => {
      onHideModal();
      // esc keyboard event shouldn't be propagated as it's already handled above
      if (
        isKeyboardEvent(event) &&
        (event.key === 'Escape' || event.key === 'Esc')
      ) {
        event.stopPropagation();
      }
    },
    [onHideModal]
  );

  return (
    <Modal
      appearance="danger"
      autoFocus
      heading={intl.formatMessage(messages.deleteCommentConfirmationHeading)}
      actions={modalActions}
      width="small"
      onClose={onCloseHandler}
    >
      {intl.formatMessage(messages.deleteCommentConfirmationMessage)}
    </Modal>
  );
};

const DeleteCommentModal = React.memo(BaseDeleteCommentModal);

export default DeleteCommentModal;
